<template>
  <div class="productDetail">
    <div class="head">
      <img @click="back" src="@/assets/images/back.png" class="arrow_img" />
      <span>{{ product.title || '产品详情' }}</span>
    </div>

    <div class="hero">
      <img v-if="product.img_url" :src="product.img_url" :alt="product.title" class="hero_img" />
      <div v-else class="hero_placeholder">暂无图片</div>
    </div>

    <div class="info_box">
      <div class="title">{{ product.title }}</div>
      <div class="price">
        <span class="price_symbol">$</span>
        <span class="price_value">{{ Number(product.price || 0).toFixed(2) }}</span>
      </div>
      <div v-if="product.power" class="bonus">
        <span>赠送算力</span>
        <span class="bonus_value">{{ Number(product.power).toFixed(2) }} T</span>
      </div>
    </div>

    <div class="desc_box">
      <div class="desc_title">商品介绍</div>
      <div v-if="productDesc" class="desc_content" v-html="productDesc"></div>
      <img
        v-else-if="contentUrl"
        :src="contentUrl"
        class="desc_image"
        @click="previewContent"
      />
      <div v-else class="desc_empty">商家暂未提供商品介绍</div>
    </div>

    <!-- 占位撑起 fixed footer 高度 -->
    <div class="footer_placeholder"></div>

    <div class="footer">
      <van-button class="buy_btn" type="primary" @click="onBuy">立即购买</van-button>
    </div>

    <ShippingAddressPopup :visible.sync="shippingAddressShow" :product="product" @confirm="onShippingConfirm" />

    <van-popup v-model="isApprove" round style="width: 60%" :close-on-click-overlay="false">
      <div class="bind_box">
        <div style="font-weight: bold"><van-loading type="spinner" /></div>
        <div class="address_field">处理中...</div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { homeApi } from '@/api/index';
import { formatAmount } from '@/utils/format.js';
import { Toast } from 'vant';
import usdt from '@/methods/usdt.js';
import packApi from '@/methods/pack.js';
import { getErc20Contract } from '@/utils/contractHelp';
import ShippingAddressPopup from './ShippingAddressPopup.vue';

export default {
  data() {
    return {
      id: '',
      product: {},
      productDesc: '',
      contentUrl: '',
      walletBalance: 0,
      payToken: undefined,
      addressData: {
        real_name: '',
        mobile: '',
        address: '',
        area: '',
        province: '',
        city: '',
        district: '',
      },
      shippingAddressShow: false,
      isApprove: false,
    };
  },
  computed: {
    ...mapState(['account']),
  },
  mounted() {
    this.id = this.$route.params.id;
    // 1. 优先尝试从 query snapshot 恢复列表跳过来时的整条产品记录
    if (this.$route.query.snapshot) {
      try {
        const decoded = decodeURIComponent(this.$route.query.snapshot);
        this.product = JSON.parse(decoded) || {};
      } catch (e) {
        this.product = {};
      }
    }
    // 2. 如有 description/content/desc 字段，预渲染
    this.productDesc = this.product.description || this.product.content || this.product.desc || '';
    // 3. 后台 products.content_url：通常是 OSS 长图，按图片渲染
    this.contentUrl = this.product.content_url || '';

    // 4. 没有 snapshot 时（比如直接刷新详情页），按 id 反查一次商品列表拿 content_url
    if (!this.contentUrl && this.id) {
      this.fetchProductDetail();
    }

    if (this.account) {
      this.loadTokenAndBalance();
    }
  },
  methods: {
    back() {
      this.$router.back();
    },
    // 直接访问详情页（无 snapshot）时，从列表接口补全 content_url / title / price 等字段
    fetchProductDetail() {
      homeApi
        .productsList({})
        .then(res => {
          if (res.code !== 200 || !Array.isArray(res.data)) return;
          const found = res.data.find(p => String(p.id) === String(this.id));
          if (!found) return;
          this.product = { ...this.product, ...found };
          this.productDesc =
            this.product.description || this.product.content || this.product.desc || '';
          this.contentUrl = this.product.content_url || '';
        })
        .catch(e => {
          console.warn('[productDetail] fetch productsList failed', e);
        });
    },
    // 点击介绍长图全屏预览
    previewContent() {
      if (!this.contentUrl) return;
      const images = [this.contentUrl];
      // vant 的 ImagePreview 在不同版本上 namespace 略有差异，做个兼容
      const Preview =
        this.$options.ImagePreview ||
        (this.$root && this.$root.ImagePreview) ||
        (typeof ImagePreview !== 'undefined' ? ImagePreview : null);
      if (Preview) {
        Preview({ images, showIndex: false, loop: false });
      } else {
        // 兜底：新窗口打开
        window.open(this.contentUrl, '_blank');
      }
    },
    async loadTokenAndBalance() {
      try {
        const tok = await packApi.getPayToken(this.account);
        this.payToken = tok;
        const erc20Contract = getErc20Contract(this.payToken);
        const bal = await erc20Contract.methods.balanceOf(this.account).call();
        this.walletBalance = (formatAmount(bal, 18) * 1).toFixed(2);
      } catch (e) {
        // pay token 拉不到不阻断下单流程
        console.warn('[productDetail] pay token / wallet balance load failed', e);
      }
    },
    onBuy() {
      this.shippingAddressShow = true;
    },
    onShippingConfirm(payload) {
      this.addressData = { ...payload.addressData };
      this.shippingAddressShow = false;
      if (this.walletBalance * 1 < this.product.price * 1) {
        this.$toast('余额不足');
        return;
      }
      this.createOrder();
    },
    // 复用现有 /order/create 下单接口，type=1 表示产品
    createOrder() {
      this.isApprove = true;
      const params = {
        ...this.addressData,
        id: this.product.id,
        type: 1,
      };
      homeApi
        .createOrder(params)
        .then(res => {
          if (res.code == 200) {
            this.payMethod(res.data, 1);
          } else {
            this.isApprove = false;
            this.$toast(res.msg);
          }
        })
        .catch(() => {
          this.isApprove = false;
        });
    },
    payMethod(obj, type) {
      usdt
        .transfer(this.account, obj.pay_address, obj.amount, this.payToken)
        .then(res => {
          const params = {
            amount: obj.amount,
            type,
            order_no: obj.order_no,
            tx_hash: res,
          };
          homeApi
            .updateOrder(params)
            .then(r => {
              this.isApprove = false;
              if (r.code == 200) {
                this.$toast(r.msg || '购买成功');
                this.$router.replace({ name: 'orders' });
              } else {
                this.$toast(r.msg);
              }
            })
            .catch(e => {
              this.isApprove = false;
              this.$toast((e && e.message) || '支付失败');
            });
        })
        .catch(e => {
          this.isApprove = false;
          this.$toast((e && e.message) || '链上支付失败');
        });
    },
  },
  watch: {
    account(val) {
      if (val) this.loadTokenAndBalance();
    },
  },
  components: { ShippingAddressPopup },
};
</script>

<style lang="less" scoped>
.head {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(#000, 0.8);
  letter-spacing: 1.5px;
  font-weight: 550;
  background: #fff;
  padding: 14px 0;
  z-index: 10;
  .arrow_img {
    position: absolute;
    left: 20px;
    width: 10px;
    height: 16px;
  }
}
.productDetail {
  padding: 50px 16px 0;
  background: #fafbfc;
  min-height: 100vh;

  .hero {
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    margin-bottom: 12px;
    box-shadow: 4px 4px 15px 0px rgba(180, 212, 212, 0.29);
    .hero_img {
      display: block;
      width: 100%;
      max-height: 320px;
      object-fit: contain;
      background: #f3f9fe;
    }
    .hero_placeholder {
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #999;
    }
  }

  .info_box {
    background: #fff;
    padding: 16px;
    border-radius: 16px;
    margin-bottom: 12px;
    box-shadow: 4px 4px 15px 0px rgba(180, 212, 212, 0.29);
    .title {
      font-size: 16px;
      color: #333;
      font-weight: 600;
      margin-bottom: 8px;
    }
    .price {
      color: #e55c29;
      font-weight: 700;
      margin-bottom: 8px;
      .price_value {
        font-size: 24px;
        margin-left: 4px;
      }
    }
    .bonus {
      font-size: 13px;
      color: #555;
      .bonus_value {
        color: #7938cf;
        margin-left: 6px;
        font-weight: 600;
      }
    }
  }

  .desc_box {
    background: #fff;
    padding: 16px;
    border-radius: 16px;
    box-shadow: 4px 4px 15px 0px rgba(180, 212, 212, 0.29);
    .desc_title {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      margin-bottom: 8px;
    }
    .desc_content {
      font-size: 13px;
      color: #555;
      line-height: 1.6;
      word-break: break-all;
    }
    .desc_image {
      display: block;
      width: 100%;
      max-width: 100%;
      border-radius: 8px;
      cursor: zoom-in;
    }
    .desc_empty {
      font-size: 13px;
      color: #999;
    }
  }

  .footer_placeholder {
    height: 80px;
  }
  .footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 12px 16px calc(env(safe-area-inset-bottom, 0px) + 12px);
    background: #fff;
    box-shadow: 0 -4px 15px rgba(180, 212, 212, 0.29);
    z-index: 10;
    .buy_btn {
      background: #7938cf;
      border: 0;
      border-radius: 12px;
      width: 100%;
      height: 44px;
      color: #fff;
      font-size: 16px;
      letter-spacing: 2px;
    }
  }
}
.bind_box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px 0;
  background-color: #000;
  .address_field {
    padding-top: 16px;
  }
}
</style>
